
<template>
  <div class="admin-panel">
    <h1>Admin Panel</h1>

    <!-- Main Tabs -->
    <div class="tabs">
      <button :class="{ active: activeTab === 'list' }" @click="activeTab = 'list'">Articles List</button>
      <button :class="{ active: activeTab === 'add' }" @click="activeTab = 'add'; currentArticle = null; selectedArticleId = null;">Add New Article</button>
      <button v-if="selectedArticleId && activeTab === 'edit'" :class="{ active: activeTab === 'edit' }" @click="activeTab = 'edit'">Edit Selected Article</button>
    </div>

    <!-- Articles List Section -->
    <div v-if="activeTab === 'list'" class="article-list-section">
      <input type="text" v-model="searchTerm" placeholder="Search by title or slug..." class="search-bar" />
      
      <p v-if="statusMessage" :class="['status-message', statusMessage.startsWith('✅') ? 'success' : 'error']">{{ statusMessage }}</p>

      <table class="article-table">
        <thead>
          <tr>
            <th>Title (EN)</th>
            <th>Created At</th>
            <th>Languages</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="article in filteredArticles" :key="article.id">
            <td>{{ article.languageVariants.en?.title || 'N/A' }}</td>
            <td>{{ new Date(article.createdAt.seconds * 1000).toLocaleDateString() }}</td>
            <td>
              <span v-for="langCode in ['en', 'nl', 'es']" :key="langCode"
                    :class="['language-badge', { 'missing': !article.languageVariants[langCode] }]">
                {{ langCode.toUpperCase() }}
              </span>
            </td>
            <td>
              <button @click="selectedArticleId = article.id; activeTab = 'edit'" class="edit">Edit</button>
              <button @click="deleteArticle(article.id)" class="delete">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add/Edit Article Section -->
    <div v-if="activeTab === 'add' || activeTab === 'edit'" class="add-edit-section">
      <h2>{{ activeTab === 'add' ? 'Add New Article' : 'Edit Article' }}</h2>
      <p v-if="statusMessage" :class="['status-message', statusMessage.startsWith('✅') ? 'success' : 'error']">{{ statusMessage }}</p>

      <div class="form-group">
        <label>Tags (comma-separated):</label>
        <input type="text" :value="activeTab === 'add' ? newArticle.tags.join(', ') : (currentArticle.tags ? currentArticle.tags.join(', ') : '')"
               @input="activeTab === 'add' ? newArticle.tags = $event.target.value.split(',').map(tag => tag.trim()) : currentArticle.tags = $event.target.value.split(',').map(tag => tag.trim())"
               placeholder="e.g., tech, reviews" />
      </div>

      <!-- Language Variant Tabs -->
      <div class="language-tabs">
        <button v-for="langCode in ['en', 'nl', 'es']" :key="langCode"
                :class="{ active: activeVariantTab === langCode }"
                @click="activeVariantTab = langCode">
          {{ langCode.toUpperCase() }}
          <span v-if="activeTab === 'edit' && !currentArticle?.languageVariants[langCode] && langCode !== 'en'" style="color: red; font-size: 0.8em;"> (Missing)</span>
          <span v-if="activeTab === 'add' && !newArticle?.languageVariants[langCode] && langCode !== 'en'" style="color: gray; font-size: 0.8em;"> (Optional)</span>
        </button>
      </div>

      <!-- Language Variant Controls -->
      <div class="language-variant-controls" v-if="activeVariantTab !== 'en'">
        <button @click="addLanguageVariant(activeVariantTab)" class="add"
                v-if="(activeTab === 'edit' && !currentArticle?.languageVariants[activeVariantTab]) || (activeTab === 'add' && !newArticle?.languageVariants[activeVariantTab])">Add {{ activeVariantTab.toUpperCase() }} Variant</button>
        <button @click="removeLanguageVariant(activeVariantTab)" class="remove"
                v-if="(activeTab === 'edit' && currentArticle?.languageVariants[activeVariantTab]) || (activeTab === 'add' && newArticle?.languageVariants[activeVariantTab] && activeVariantTab !== 'en')">Remove {{ activeVariantTab.toUpperCase() }} Variant</button>
      </div>
      
      <!-- Variant Editor -->
      <div v-if="activeVariant">
        <div class="form-group">
          <label>Title ({{ activeVariantTab.toUpperCase() }}):</label>
          <input type="text" v-model="activeVariant.title"
                 @input="generateSlugForVariant(activeArticle, activeVariantTab)" />
        </div>
        <div class="form-group">
          <label>Slug ({{ activeVariantTab.toUpperCase() }}):</label>
          <input type="text" v-model="activeVariant.slug"
                 @input="activeVariant.slug = slugify($event.target.value)" />
          <button @click="generateSlugForVariant(activeArticle, activeVariantTab)" style="margin-left: 10px;">Generate Slug</button>
          <p v-if="(activeTab === 'edit' && currentArticle?.languageVariants[activeVariantTab] && currentArticle.languageVariants[activeVariantTab].slug) || (activeTab === 'add' && newArticle?.languageVariants[activeVariantTab] && newArticle.languageVariants[activeVariantTab].slug)">
            Preview: <a :href="`/${activeVariantTab}/article/${activeTab === 'add' ? newArticle.languageVariants[activeVariantTab].slug : currentArticle.languageVariants[activeVariantTab].slug}`" target="_blank">{{ activeVariantTab.toUpperCase() }} Article</a>
          </p>
        </div>
        <div class="form-group">
          <label>Meta Description ({{ activeVariantTab.toUpperCase() }}):</label>
          <textarea v-model="activeVariant.metaDescription" rows="3"></textarea>
        </div>
        <div class="form-group">
          <label>Content ({{ activeVariantTab.toUpperCase() }}):</label>
          <RichTextEditor v-model="activeVariant.content" />
        </div>
      </div>
      <div v-else-if="activeTab === 'edit' && !currentArticle?.languageVariants[activeVariantTab] && activeVariantTab !== 'en'">
          <p>No {{ activeVariantTab.toUpperCase() }} variant exists. Click "Add {{ activeVariantTab.toUpperCase() }} Variant" above to create one.</p>
      </div>
      <div v-else-if="activeTab === 'add' && !newArticle?.languageVariants[activeVariantTab] && activeVariantTab !== 'en'">
          <p>No {{ activeVariantTab.toUpperCase() }} variant has been added yet. Click "Add {{ activeVariantTab.toUpperCase() }} Variant" above to create one.</p>
      </div>

      <div class="form-actions">
        <button v-if="activeTab === 'add'" @click="addArticle">Add Article</button>
        <button v-if="activeTab === 'edit'" @click="updateArticle">Save Changes</button>
        <button v-if="activeTab === 'edit'" @click="deleteArticle(selectedArticleId)" class="delete">Delete Article</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue';
import { collection, doc, setDoc, getDocs, updateDoc, deleteDoc, query, orderBy } from "firebase/firestore"; // Added query, orderBy
import { db } from "@/firebaseConfig";
import RichTextEditor from '@/components/RichTextEditor.vue'; // Import new RichTextEditor
import { slugify } from '../utils/slugify';

export default {
  components: { RichTextEditor }, // Only RichTextEditor is registered
  setup() {
    const activeTab = ref("list"); // New default tab for listing articles
    const statusMessage = ref("");
    const articles = ref([]); // Changed from posts to articles
    const searchTerm = ref('');

    const selectedArticleId = ref(null);
    const currentArticle = ref(null); // Changed from currentPost to currentArticle
    const activeVariantTab = ref('en'); // Default language tab for editing

    // Reactive state for a new article
    const newArticle = ref({
      defaultLang: 'en',
      tags: [],
      languageVariants: {
        en: {
          title: '',
          slug: '',
          metaDescription: '',
          content: ''
        },
      }
    });

    // Computed ref for the currently active article (either currentArticle or newArticle)
    const activeArticle = computed(() => {
      return activeTab.value === 'add' ? newArticle.value : currentArticle.value;
    });

    // Computed ref for the currently active language variant
    const activeVariant = computed(() => {
      return activeArticle.value?.languageVariants[activeVariantTab.value];
    });

        const filteredArticles = computed(() => {

            if (!searchTerm.value) {

                return articles.value;

            }

            const lowerCaseSearchTerm = searchTerm.value.toLowerCase();

            return articles.value.filter(article => {
            // Search across all available language variants' titles and slugs
            for (const langCode in article.languageVariants) {
                const variant = article.languageVariants[langCode];
                if (variant.title.toLowerCase().includes(lowerCaseSearchTerm) ||
                    variant.slug.toLowerCase().includes(lowerCaseSearchTerm)) {
                    return true;
                }
            }
            return false;
        });
    });

    // --- Methods ---

    const generateSlugForVariant = (articleRef, langCode) => {
      if (articleRef && articleRef.languageVariants[langCode]) {
        articleRef.languageVariants[langCode].slug = slugify(articleRef.languageVariants[langCode].title);
      }
    };

    const fetchArticles = async () => {
      try {
        // Order by 'createdAt' field in descending order (newest first)
        const q = query(collection(db, "articles"), orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        articles.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('Fetched articles:', articles.value);
      } catch (e) {
        statusMessage.value = "❌ Error fetching articles: " + e.message;
        console.error("Error fetching articles:", e);
      }
    };

    const loadArticle = async () => {
        if (!selectedArticleId.value) {
            currentArticle.value = null;
            return;
        }
        const articleToLoad = articles.value.find(a => a.id === selectedArticleId.value);
        if (articleToLoad) {
            currentArticle.value = JSON.parse(JSON.stringify(articleToLoad)); // Deep copy to prevent direct mutation
            activeVariantTab.value = currentArticle.value.defaultLang || 'en'; // Set default tab to article's default lang
        }
    };

    const addLanguageVariant = (langCode) => {
        if (activeArticle.value && !activeArticle.value.languageVariants[langCode]) {
            activeArticle.value.languageVariants[langCode] = {
                title: '',
                slug: '',
                metaDescription: '',
                content: ''
            };
            activeVariantTab.value = langCode;
        }
    };

    const removeLanguageVariant = (langCode) => {
        if (activeArticle.value && activeArticle.value.languageVariants[langCode] && langCode !== activeArticle.value.defaultLang) {
            delete activeArticle.value.languageVariants[langCode];
            if (activeVariantTab.value === langCode) {
                activeVariantTab.value = activeArticle.value.defaultLang || 'en';
            }
        }
    };

    const updateArticle = async () => {
      if (!currentArticle.value || !selectedArticleId.value) return;

      statusMessage.value = "";
      // Basic validation: English variant (title, slug, meta description, content) is required.
      const enVariant = currentArticle.value.languageVariants.en;
      if (!enVariant || !enVariant.title || !enVariant.slug || !enVariant.metaDescription || !enVariant.content) {
          statusMessage.value = "❌ English variant (title, slug, meta description, content) is required.";
          return;
      }
      
      // Slug length validation
      for (const langCode in currentArticle.value.languageVariants) {
          const variant = currentArticle.value.languageVariants[langCode];
          if (variant.slug && variant.slug.length > 120) {
              statusMessage.value = `❌ Slug for ${langCode.toUpperCase()} is too long (max 120 characters).`;
              return;
          }
      }

      // TODO: Add slug uniqueness validation across all articles and languages
      
      try {
        const articleRef = doc(db, "articles", selectedArticleId.value);
        const updatedData = {
          ...currentArticle.value, // Use activeArticle.value directly
          updatedAt: new Date(),
        };
        // Remove 'id' from the data being sent to Firestore
        delete updatedData.id; 

        await updateDoc(articleRef, updatedData);
        statusMessage.value = "✅ Article updated!";
        await fetchArticles(); // Refresh list
      } catch (e) {
        statusMessage.value = "❌ Error updating article: " + e.message;
        console.error("Error updating article:", e);
      }
    };

    const deleteArticle = async (id) => {
      statusMessage.value = "";
      if (!confirm('Are you sure you want to delete this article?')) return;
      try {
        await deleteDoc(doc(db, "articles", id));
        statusMessage.value = "🗑️ Article deleted!";
        selectedArticleId.value = null;
        currentArticle.value = null;
        await fetchArticles(); // Refresh list
      } catch (e) {
        statusMessage.value = "❌ Error deleting article: " + e.message;
        console.error("Error deleting article:", e);
      }
    };

    const addArticle = async () => {
      statusMessage.value = "";
      // Validation for new article (at least EN variant must be complete)
      const enVariant = newArticle.value.languageVariants.en;
      if (!enVariant || !enVariant.title || !enVariant.slug || !enVariant.metaDescription || !enVariant.content) {
          statusMessage.value = "❌ English variant (title, slug, meta description, content) is required for a new article.";
          return;
      }

      // Slug length validation
      for (const langCode in newArticle.value.languageVariants) {
          const variant = newArticle.value.languageVariants[langCode];
          if (variant.slug && variant.slug.length > 120) {
              statusMessage.value = `❌ Slug for ${langCode.toUpperCase()} is too long (max 120 characters).`;
              return;
          }
      }
      // TODO: Add slug uniqueness validation across all articles and languages
      
      try {
        const newDocRef = doc(collection(db, "articles"));
        const dataToAdd = {
            ...newArticle.value, // Use newArticle.value directly
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        await setDoc(newDocRef, dataToAdd);

        statusMessage.value = "✅ Article added successfully!";
        // Reset form
        newArticle.value = {
            defaultLang: 'en',
            tags: [],
            languageVariants: {
                en: { title: '', slug: '', metaDescription: '', content: '' },
            }
        };
        await fetchArticles(); // Refresh list
        activeTab.value = 'list'; // Go back to list view
      } catch (error) {
        statusMessage.value = "❌ Error adding article: " + error.message;
        console.error("Error adding article:", error);
      }
    };

    onMounted(async () => {
      await fetchArticles();
    });

    watch(selectedArticleId, async () => {
      await loadArticle();
    });

    return {
      activeTab,
      statusMessage,
      articles,
      searchTerm,
      filteredArticles,
      selectedArticleId,
      currentArticle,
      activeVariantTab,
      newArticle,
      activeArticle,
      activeVariant,
      generateSlugForVariant,
      fetchArticles,
      loadArticle,
      addArticle,
      updateArticle,
      deleteArticle,
      addLanguageVariant,
      removeLanguageVariant,
    };
  },
};
</script>

<style scoped>
.admin-panel {
  max-width: 1200px;
  margin: 30px auto;
  padding: 20px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

h1 {
  color: #333;
  text-align: center;
  margin-bottom: 30px;
}

.tabs {
  display: flex;
  margin-bottom: 20px;
  border-bottom: 2px solid #eee;
}

.tabs button {
  background: none;
  border: none;
  padding: 10px 20px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #555;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px; /* to make border-bottom overlap */
}

.tabs button:hover:not(.active) {
  color: #e91e63;
}

.tabs button.active {
  color: #e91e63;
  border-bottom: 2px solid #e91e63;
  font-weight: 600;
}

.article-list-section, .add-edit-section {
  padding: 20px 0;
}

.search-bar {
  width: 100%;
  padding: 10px;
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
}

.article-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.article-table th, .article-table td {
  border: 1px solid #eee;
  padding: 12px;
  text-align: left;
}

.article-table th {
  background-color: #f8f8f8;
  font-weight: 600;
  color: #333;
}

.article-table tr:nth-child(even) {
  background-color: #fdfdfd;
}

.article-table tr:hover {
  background-color: #f0f0f0;
}

.article-table button {
  padding: 8px 12px;
  margin-right: 5px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.article-table button.edit {
  background-color: #2196f3;
  color: white;
}

.article-table button.edit:hover {
  background-color: #1976d2;
}

.article-table button.delete {
  background-color: #f44336;
  color: white;
}

.article-table button.delete:hover {
  background-color: #d32f2f;
}

.language-badge {
  display: inline-block;
  padding: 3px 8px;
  margin-right: 5px;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: 600;
  color: white;
  background-color: #4caf50; /* Green for available */
}
.language-badge.missing {
    background-color: #f44336; /* Red for missing */
}

/* Form Styles */
.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #333;
}

.form-group input[type="text"],
.form-group textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
}

.form-group input[type="checkbox"] {
  margin-right: 8px;
}

.form-actions {
  margin-top: 20px;
  text-align: right;
}

.form-actions button {
  padding: 10px 20px;
  background-color: #e91e63;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.form-actions button:hover {
  background-color: #c2185b;
}

.status-message {
  margin-top: 20px;
  padding: 10px;
  border-radius: 8px;
  font-weight: 600;
}

.status-message.success {
  background-color: #e8f5e9;
  color: #4caf50;
}

.status-message.error {
  background-color: #ffebee;
  color: #f44336;
}

.language-tabs {
    display: flex;
    margin-bottom: 15px;
    border-bottom: 1px solid #ddd;
}

.language-tabs button {
    background: none;
    border: none;
    padding: 8px 15px;
    font-size: 0.95rem;
    cursor: pointer;
    color: #666;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
}

.language-tabs button.active {
    color: #e91e63;
    border-bottom: 2px solid #e91e63;
    font-weight: 600;
}

.language-variant-controls {
    display: flex;
    gap: 10px;
    margin-bottom: 15px;
}
.language-variant-controls button {
    padding: 8px 12px;
    border: 1px solid #ccc;
    border-radius: 5px;
    background-color: #f0f0f0;
    cursor: pointer;
}
.language-variant-controls button.add {
    background-color: #4caf50;
    color: white;
    border-color: #4caf50;
}
.language-variant-controls button.remove {
    background-color: #f44336;
    color: white;
    border-color: #f44336;
}
</style>
